import { useEffect } from 'react';
import { captureException } from '@sentry/react';

import { clearApolloCache } from '@/shared/api';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import { useReportStore } from '@/entities/report';
import type { FallbackErrorProps } from './index';

/**
 * Backs the error-boundary fallback of a report tab, reporting the failure and offering a reload. Cancels the in-flight API calls and sends the error to Sentry whenever a new error arrives, and exposes a reset handler that clears the Apollo cache, bumps the store snapshot action and resets the boundary.
 *
 * @param props - The caught error and the resetErrorBoundary callback from the error boundary.
 * @returns An object carrying the onClickReset handler.
 */
const useFallbackError = ({
  error,
  resetErrorBoundary,
}: Pick<FallbackErrorProps, 'error' | 'resetErrorBoundary'>) => {
  const setActionSnapshot = useReportStore((s) => s.actions.setActionSnapshot);

  useEffect(() => {
    if (error) {
      cancelAllAPI();
      captureException(error);
    }
  }, [error]);

  /**
   * Recovers a report tab after its error boundary has tripped. Clears the Apollo cache, sets the store snapshot action to 2 and calls resetErrorBoundary so the tab re-renders.
   *
   * @returns A promise resolving once the cache is cleared and the boundary has been reset.
   */
  const onClickReset = async () => {
    await clearApolloCache();
    // resetEcgViewerConfigStore(););
    setActionSnapshot(2);
    resetErrorBoundary();
  };

  return {
    onClickReset,
  };
};

export default useFallbackError;
